import React, { useState, useCallback } from 'react';
import { Button, Space, message, Tooltip } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { ImportModal } from './ImportModal';

interface ExportImportButtonsProps {
  onImportSuccess?: () => void;
}

export const ExportImportButtons: React.FC<ExportImportButtonsProps> = ({ onImportSuccess }) => {
  const [exporting, setExporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response = await gamificationApi.exportConfig();

      if (!response.success) {
        throw new Error(response.message || 'Error al exportar');
      }

      const exportData = response.data;

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `gamification-export-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const summary = [
        exportData.levels?.length ? `${exportData.levels.length} niveles` : null,
        exportData.rules?.length ? `${exportData.rules.length} reglas` : null,
        exportData.objectives?.length ? `${exportData.objectives.length} objetivos` : null,
        exportData.redemptionConfigs?.length ? `${exportData.redemptionConfigs.length} canjes` : null,
      ].filter(Boolean).join(', ');

      message.success(`Exportado: ${summary || 'configuracion'}`);
    } catch (e: any) {
      console.error('Export error:', e);
      message.error('Error al exportar: ' + (e.message || 'Error desconocido'));
    } finally {
      setExporting(false);
    }
  }, []);

  const handleImportSuccess = useCallback(() => {
    if (onImportSuccess) {
      onImportSuccess();
    }
  }, [onImportSuccess]);

  return (
    <>
      <Space size={8}>
        <Tooltip title="Exportar configuracion de gamificacion a JSON">
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
          >
            Exportar
          </Button>
        </Tooltip>

        <Tooltip title="Importar configuracion desde archivo JSON">
          <Button
            icon={<UploadOutlined />}
            onClick={() => setImportModalOpen(true)}
          >
            Importar
          </Button>
        </Tooltip>
      </Space>

      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};
